import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { apiURL } from "../config/config";
import { ClipLoader } from "react-spinners";
import BackButton from "../components/BackButton";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";

function UserManagement() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${apiURL}users`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUsers(response.data);
      } catch (error) {
        console.error("Error fetching users:", error);
        toast.error("โหลดรายชื่อผู้ใช้ไม่สำเร็จ");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleEditUser = (user) => {
    navigate(`/edit-user/${user._id}`);
  };

  const handleDeleteUser = async (userId, username) => {
    Swal.fire({
      title: `แน่ใจหรือไม่ที่จะลบ User : ${username} ?`,
      text: "เมื่อลบแล้วจะไม่สามารถกู้คืนได้",
      icon: "warning",
      background: "#1f2937",
      color: "#fff",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "ใช่, ลบเลย!",
      cancelButtonText: "ยกเลิก",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("token");
          await axios.delete(`${apiURL}users/${userId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          toast.success("ลบผู้ใช้สำเร็จ");
          setUsers((prevUsers) =>
            prevUsers.filter((user) => user._id !== userId)
          );
        } catch (error) {
          console.error("Error deleting user:", error);
          toast.error("ไม่สามารถลบผู้ใช้ได้");
        }
      }
    });
  };

  const filteredUsers = users.filter((user) => {
    const lowerSearch = searchTerm.toLowerCase();
    const combinedFields = `${user.username} ${user.firstName} ${user.lastName} ${
      user.nickname || ""
    } ${user.email || ""}`.toLowerCase();
    return combinedFields.includes(lowerSearch);
  });

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  return (
    <div className="min-h-screen px-4 sm:px-6 py-6 relative bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <BackButton label="ย้อนกลับ" />
        </div>

        <div>
          <h1 className="text-[26px] font-semibold text-gray-900 dark:text-gray-100 leading-tight">
            User Management
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
            จัดการผู้ใช้งานในระบบ เพิ่ม ลบ หรือแก้ไขสิทธิ์ของผู้ใช้
          </p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <input
          type="text"
          placeholder="ค้นหาชื่อ, อีเมล, ชื่อเล่น หรือ Username..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          className="border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 w-full md:w-1/2 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
        />

        <button
          onClick={() => navigate("/create-user")}
          className="flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-medium px-5 py-2.5 rounded-md shadow-sm transition-all duration-200"
        >
          <FaPlus size={14} />
          <span>เพิ่มผู้ใช้ใหม่</span>
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <ClipLoader size={50} color="#6366F1" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {currentUsers.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center w-full py-10">
                ไม่พบผู้ใช้งานในระบบ
              </p>
            ) : (
              currentUsers.map((user) => (
                <div
                  key={user._id}
                  className="relative bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
                >
                  <div className="bg-gradient-to-r from-indigo-500 to-blue-500 p-4">
                    <h2 className="text-lg font-semibold text-white">
                      {user.firstName} {user.lastName}
                    </h2>
                    <p className="text-sm text-indigo-100">
                      {user.position || "-"}
                    </p>
                  </div>

                  <div className="absolute top-3 right-3 flex space-x-2">
                    <button
                      onClick={() => handleEditUser(user)}
                      className="text-white bg-blue-500 hover:bg-blue-600 p-2 rounded-full shadow-sm transition"
                      title="แก้ไขผู้ใช้"
                    >
                      <FaEdit size={14} />
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user._id, user.username)}
                      className="text-white bg-red-500 hover:bg-red-600 p-2 rounded-full shadow-sm transition"
                      title="ลบผู้ใช้"
                    >
                      <FaTrash size={14} />
                    </button>
                  </div>

                  <div className="p-4 space-y-1 text-sm text-gray-700 dark:text-gray-300">
                    <p>
                      <span className="font-medium text-gray-600 dark:text-gray-400">
                        Username:
                      </span>{" "}
                      {user.username}
                    </p>
                    <p>
                      <span className="font-medium text-gray-600 dark:text-gray-400">
                        ชื่อเล่น:
                      </span>{" "}
                      {user.nickname || "-"}
                    </p>
                    <p>
                      <span className="font-medium text-gray-600 dark:text-gray-400">
                        Company:
                      </span>{" "}
                      {user.company?.toUpperCase()}
                    </p>
                    <p>
                      <span className="font-medium text-gray-600 dark:text-gray-400">
                        Department:
                      </span>{" "}
                      {user.department || "-"}
                    </p>
                    <p>
                      <span className="font-medium text-gray-600 dark:text-gray-400">
                        Role:
                      </span>{" "}
                      {user.role}
                    </p>
                    <p>
                      <span className="font-medium text-gray-600 dark:text-gray-400">
                        Level:
                      </span>{" "}
                      {user.level}
                    </p>
                    <p>
                      <span className="font-medium text-gray-600 dark:text-gray-400">
                        Team:
                      </span>{" "}
                      {user.team || "-"}
                    </p>
                    <p>
                      <span className="font-medium text-gray-600 dark:text-gray-400">
                        Team Group:
                      </span>{" "}
                      {user.teamGroup || "-"}
                    </p>
                    <p>
                      <span className="font-medium text-gray-600 dark:text-gray-400">
                        Team Role:
                      </span>{" "}
                      {user.teamRole || "-"}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center items-center mt-8 gap-4">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 text-sm rounded-md bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-sm rounded-md bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default UserManagement;
